import { useEffect, useState } from 'react';
import { maintenanceStatus, type MaintenanceStatus } from '../api.ts';

/**
 * "System updating" - said while it is true, and only while it is true.
 *
 * Misha, 2026-09-09, after Henry tested during a Verus reingest and filed the result as a
 * defect: "if user hits Search, while u are in the middle of a system update where the
 * quality of the data may be impacted, it should display a warning... this way we know
 * when it's safe to run queries and when it's questionable time."
 *
 * The window is dangerous precisely because the product does NOT look broken during it. A
 * lender mid-reingest has its old documents superseded and its new ones not yet active, so
 * the sweep finds no text for it and says so - in the same words it uses for a lender we
 * genuinely hold nothing on. A broker cannot tell those apart, and neither could Henry.
 *
 * WHAT IT DOES NOT SAY IS AN ETA. A guideline ingest is a few hundred model calls whose
 * length depends on page count, page complexity and how busy the API is: today's 250-page
 * seller guide took about forty minutes, a 21-page matrix took four. Any number this could
 * print would be invented, and a wrong ETA is worse than none - "8 minutes remaining" that
 * lapses reads as broken, where "started 12 minutes ago" reads as working. So it shows
 * elapsed time and names the lenders, and lets the reader judge.
 *
 * It also does not block the Search button. Most files never touch the lender being
 * reingested, and a broker who can see which lenders are moving can decide for themselves
 * - which is the whole point of naming them.
 */

const POLL_MS = 30_000;

function elapsed(startedAt: string | null): string {
  if (!startedAt) return '';
  const ms = Date.now() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just started';
  if (mins === 1) return 'started a minute ago';
  if (mins < 60) return `started ${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  return hours === 1 ? 'started over an hour ago' : `started over ${hours} hours ago`;
}

export default function UpdatingBanner() {
  const [status, setStatus] = useState<MaintenanceStatus | null>(null);

  useEffect(() => {
    let live = true;
    const check = () => {
      // A status check must never be able to break the page it decorates: on any failure
      // the banner simply does not render, which is the same as the common case.
      void maintenanceStatus().then((s) => { if (live) setStatus(s); }).catch(() => {});
    };
    check();
    const t = setInterval(check, POLL_MS);
    return () => { live = false; clearInterval(t); };
  }, []);

  if (!status?.updating) return null;

  const many = status.lenders.length > 1;
  const when = elapsed(status.startedAt);

  return (
    <div
      className="card"
      role="status"
      style={{
        maxWidth: 940, margin: '0 auto 18px', borderColor: 'var(--amber)',
        background: 'var(--amber-soft, #fff8e6)', display: 'flex', gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 18, lineHeight: '22px' }}>⚠️</span>
      <div style={{ fontSize: 13, lineHeight: 1.6 }}>
        <strong>System updating{when ? ` — ${when}` : ''}.</strong>{' '}
        {/*
          The lenders are named because that is the only part a broker can act on: a file
          that never reaches this lender is unaffected, and "one lender is updating" without
          saying which leaves them unable to judge that.
        */}
        We are loading new documents for{' '}
        <strong>{status.lenders.join(', ')}</strong>. Until that finishes,{' '}
        {many ? 'those lenders' : 'that lender'} may come back thin or missing from an
        answer — the old documents have been retired and the new ones are still being read.
        Everything else is unaffected, and a search that does not reach{' '}
        {many ? 'them' : 'it'} is fine to run now.
      </div>
    </div>
  );
}
